import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useRef } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { Outlet } from 'react-router';
import styled from 'styled-components';

import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { WithScrollButtons } from '@sorare/core/src/atoms/navigation/WithScrollButtons';
import { BrandM } from '@sorare/core/src/atoms/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';

import { WatchlistItem } from '#src/components/watchlists/WatchlistItem/index.tsx';

import type {
  WatchlistsLayoutQuery,
  WatchlistsLayoutQueryVariables,
} from './__generated__/index.graphql.ts';

const WATCHLISTS_LAYOUT_QUERY = gql`
  query WatchlistsLayoutQuery(
    $sport: Sport!
    $after: String
    $id: ID!
    $withId: Boolean!
  ) {
    market {
      id
      watchlist(id: $id) @include(if: $withId) {
        slug
        public
        user {
          slug
        }
      }
      publicWatchlists(sport: $sport, after: $after, first: 10) {
        nodes {
          slug
          ...WatchlistItem_watchlist
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${WatchlistItem.fragments.watchlist}
` as TypedDocumentNode<WatchlistsLayoutQuery, WatchlistsLayoutQueryVariables>;

const messages = defineMessages({
  watchlistsTitle: {
    id: 'WatchlistsLayout.watchlists.title',
    defaultMessage: 'Popular watchlists',
  },
  publicWatchlistTitle: {
    id: 'WatchlistsLayout.publicWatchlist.title',
    defaultMessage: 'You may also like',
  },
});

const ItemWrapper = styled(Vertical)`
  min-width: 300px;
  height: 100%;
`;

const ItemsContainer = styled(Horizontal).attrs({ gap: 2 })`
  align-items: flex-start;
`;

const Root = styled(Vertical)`
  gap: var(--quadruple-unit);
  padding-bottom: var(--triple-unit);
`;

type Props = {
  id?: string;
};

export const WatchlistsLayout = ({ id }: Props) => {
  const wrapperRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const { sport } = useSportContext();

  const { isMe } = useCurrentUserContext();

  const { data, loadMore, loading } = usePaginatedQuery(
    WATCHLISTS_LAYOUT_QUERY,
    {
      variables: { sport, id: id || '', withId: !!id },
      connection: 'WatchlistConnection',
      skip: !sport,
    }
  );
  const { publicWatchlists } = data?.market || {};
  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        after: publicWatchlists?.pageInfo?.endCursor,
      });
    },
    !!publicWatchlists?.pageInfo?.hasNextPage,
    loading
  );

  const watchlist = data?.market?.watchlist;
  const isNotMine = !watchlist || !isMe(watchlist?.user);

  return (
    <FullWidthContainer.NoPadding>
      <Root gap={4}>
        <Outlet />
        {!!isNotMine && (
          <FullWidthContainer>
            <Vertical gap={3}>
              <BrandM>
                <FormattedMessage
                  {...(watchlist
                    ? messages.publicWatchlistTitle
                    : messages.watchlistsTitle)}
                />
              </BrandM>
              <WithScrollButtons
                wrapperRef={wrapperRef}
                scrollContainerRef={scrollContainerRef}
                contained
              >
                <FullViewportScroll ref={scrollContainerRef}>
                  <ItemsContainer>
                    {publicWatchlists?.nodes.map(w => (
                      <ItemWrapper key={w.slug}>
                        <WatchlistItem watchlist={w} />
                      </ItemWrapper>
                    ))}
                    <ItemWrapper center>
                      <InfiniteScrollLoader />
                    </ItemWrapper>
                  </ItemsContainer>
                </FullViewportScroll>
              </WithScrollButtons>
            </Vertical>
          </FullWidthContainer>
        )}
      </Root>
    </FullWidthContainer.NoPadding>
  );
};
